import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Segmented, Button, Typography, Space, Flex, Switch } from "antd";
import { EyeOutlined, SaveOutlined, CopyOutlined } from "@ant-design/icons";
import useClinicalForms from "../../../../../../hooks/useClinicalForms";
import useUrlParam from "../../../../../../hooks/useUrlParam";
import {
  slugify,
  isIaForm,
  isReviewForm,
} from "../../../../../../mappers/clinicalForms.mappers";
import FormTabs from "./FormTabs";
import SectionTabs from "./SectionTabs";
import SectionEditor from "./SectionEditor";
import PreviewModal from "../modals/PreviewModal";
import DuplicateFormModal from "../modals/DuplicateFormModal";
import {
  PATIENT_MODES,
  MAX_FORMS_PER_MODE,
  newForm,
  newSection,
  buildFormPayload,
  cloneFormWithNewIds,
} from "../../../../../../domain/clinicalForms";
import {
  clinicalFormSchema,
  validateNestingDepth,
  formatSaveError,
} from "../../../../../../validation";
import {
  FormValidationProvider,
  buildErrorMap,
  firstErroredSectionIndex,
} from "./ValidationContext";
import { message } from "antd";

const isPersistedId = (id) => /^\d+$/.test(String(id));

const FormBuilder = ({ role, onChange, roles = [], updateRole }) => {
  const [formParam, setFormParam] = useUrlParam("form", { replace: true });
  const [localFormId, setLocalFormId] = useState();
  const [activeSectionId, setActiveSectionId] = useState();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [errors, setErrors] = useState(null);
  const {
    createForm,
    updateForm: saveForm,
    setFormActive,
    setFormRepeatable,
    updating,
  } = useClinicalForms();

  const availableModes = useMemo(
    () =>
      role.value === "Nurse"
        ? PATIENT_MODES.filter((m) => m.value === "IP")
        : PATIENT_MODES,
    [role.value]
  );

  useEffect(() => {
    if (role.value === "Nurse" && role.mode !== "IP") {
      onChange({ ...role, mode: "IP" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.value, role.mode]);

  const formsForMode = useMemo(() => {
    const inMode = role.forms.filter((f) => f.mode === role.mode);
    if (role.system && role.mode === "IP") {
      return inMode.filter((f) => !isIaForm(f) && !isReviewForm(f));
    }
    const ia = [];
    const review = [];
    const other = [];
    inMode.forEach((f) => {
      if (isIaForm(f)) ia.push(f);
      else if (isReviewForm(f)) review.push(f);
      else other.push(f);
    });
    return [...ia, ...review, ...other];
  }, [role.forms, role.mode, role.system]);

  const activeForm = useMemo(() => {
    const fromUrl = formParam && formsForMode.find((f) => f.id === formParam);
    const fromLocal =
      localFormId && formsForMode.find((f) => f.id === localFormId);
    return fromUrl || fromLocal || formsForMode[0];
  }, [formsForMode, formParam, localFormId]);

  const setActiveFormId = useCallback(
    (id) => {
      if (isPersistedId(id)) {
        setLocalFormId(undefined);
        setFormParam(id);
      } else {
        setLocalFormId(id);
        if (formParam) setFormParam(null);
      }
    },
    [formParam, setFormParam]
  );

  useEffect(() => {
    if (!activeForm) return;
    if (!activeForm.sections.find((s) => s.id === activeSectionId)) {
      setActiveSectionId(activeForm.sections[0]?.id);
    }
  }, [activeForm, activeSectionId]);

  const activeSection = activeForm?.sections.find(
    (s) => s.id === activeSectionId
  );
  const activeSectionIdx = activeForm?.sections.findIndex(
    (s) => s.id === activeSection?.id
  );

  const erroredSectionIds = useMemo(() => {
    if (!errors || !activeForm) return new Set();
    const ids = new Set();
    activeForm.sections.forEach((section, idx) => {
      const prefix = `sections.${idx}`;
      if (
        errors.has(`${prefix}.fields`) ||
        errors.has(`${prefix}.title`) ||
        [...errors.keys()].some((key) => key.startsWith(`${prefix}.fields.`))
      ) {
        ids.add(section.id);
      }
    });
    return ids;
  }, [errors, activeForm]);

  const clearPath = useCallback((key) => {
    setErrors((prev) => {
      if (!prev || !prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next.size ? next : null;
    });
  }, []);

  // Drop stale errors when switching forms, not sections — save validation
  // may jump to another section and its inline errors must stay visible.
  useEffect(() => {
    setErrors(null);
  }, [activeForm?.id]);

  const updateForm = (formId, next) => {
    onChange({
      ...role,
      forms: role.forms.map((f) => (f.id === formId ? next : f)),
    });
  };

  const updateSection = (next) => {
    if (!activeForm) return;
    updateForm(activeForm.id, {
      ...activeForm,
      sections: activeForm.sections.map((s) =>
        s.id === activeSectionId ? next : s
      ),
    });
  };

  const addForm = () => {
    if (formsForMode.length >= MAX_FORMS_PER_MODE) {
      message.warning(
        `You can only add up to ${MAX_FORMS_PER_MODE} forms per mode.`
      );
      return;
    }
    const form = newForm(`Form ${formsForMode.length + 1}`, role.mode);
    onChange({ ...role, forms: [...role.forms, form] });
    setActiveFormId(form.id);
  };

  const addSection = () => {
    if (!activeForm) return;
    const section = newSection(activeForm.sections.length + 1);
    updateForm(activeForm.id, {
      ...activeForm,
      sections: [...activeForm.sections, section],
    });
    setActiveSectionId(section.id);
  };

  const deleteSection = (sectionId) => {
    if (!activeForm) return;
    const remaining = activeForm.sections.filter((s) => s.id !== sectionId);
    updateForm(activeForm.id, { ...activeForm, sections: remaining });
    if (sectionId === activeSectionId) setActiveSectionId(remaining[0]?.id);
  };

  const persistForm = async (form) => {
    const schema = buildFormPayload(form);
    if (isPersistedId(form.id)) {
      return saveForm(form.id, {
        name: form.name,
        mode: form.mode,
        schema,
      });
    }
    const roleBinding = isPersistedId(role.id)
      ? { clinicalRoleId: Number(role.id) }
      : { builtinRole: role.value };
    return createForm({
      name: form.name,
      code: slugify(form.name) || `form_${Date.now()}`,
      mode: form.mode,
      schema,
      isActive: form.active,
      isRepeatable: form.repeatable,
      ...roleBinding,
    });
  };

  const handleSave = async () => {
    if (!activeForm) return;
    const parsed = clinicalFormSchema.safeParse(activeForm);
    if (!parsed.success) {
      setErrors(buildErrorMap(parsed.error));
      const targetIdx = firstErroredSectionIndex(parsed.error);
      const targetSection = targetIdx >= 0 ? activeForm.sections[targetIdx] : null;
      if (targetSection && targetSection.id !== activeSectionId) {
        setActiveSectionId(targetSection.id);
      }
      message.error(formatSaveError(parsed.error, activeForm));
      return;
    }
    const depthError = validateNestingDepth(activeForm);
    if (depthError) {
      message.error(depthError);
      return;
    }
    setErrors(null);
    try {
      const saved = await persistForm(activeForm);
      const previousId = activeForm.id;
      updateForm(previousId, { ...activeForm, ...saved });
      if (saved.id !== previousId) setActiveFormId(saved.id);
      message.success("Form saved");
    } catch {
      /* error toast handled in hook */
    }
  };

  const handleDuplicate = async ({ targetRoleIds, name }) => {
    if (!activeForm || !targetRoleIds?.length) return;
    setDuplicating(true);
    let successCount = 0;
    try {
      for (const targetRoleId of targetRoleIds) {
        const targetRole = roles.find((r) => r.id === targetRoleId);
        if (!targetRole) continue;
        const clone = cloneFormWithNewIds(activeForm, {
          name,
          mode: activeForm.mode,
          system: false,
          repeatable: activeForm.repeatable,
        });
        const roleBinding = isPersistedId(targetRole.id)
          ? { clinicalRoleId: Number(targetRole.id) }
          : { builtinRole: targetRole.value };
        try {
          const saved = await createForm({
            name: clone.name,
            code: slugify(clone.name) || `form_${Date.now()}`,
            mode: clone.mode,
            schema: buildFormPayload(clone),
            isActive: true,
            isRepeatable: !!clone.repeatable,
            ...roleBinding,
          });
          const nextForm = { ...clone, ...saved };
          updateRole?.({
            ...targetRole,
            forms: [...(targetRole.forms || []), nextForm],
          });
          successCount += 1;
        } catch {
          /* error toast handled in hook */
        }
      }
      if (successCount > 0) {
        message.success(
          successCount === targetRoleIds.length
            ? `Form duplicated to ${successCount} role${successCount === 1 ? "" : "s"}`
            : `Duplicated to ${successCount} of ${targetRoleIds.length} roles`
        );
        setDuplicateOpen(false);
      }
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <FormValidationProvider errors={errors} clearPath={clearPath}>
      <Flex vertical style={{ height: "100%", overflowY: "auto", background: "#f7f8fa" }}>
        <div style={{ background: "#ffffff", padding: "20px 32px 12px" }}>
          <Flex justify="space-between" align="center">
            <Typography.Title level={5} style={{ margin: 0 }}>
              {role.name}
            </Typography.Title>
            <Segmented
              options={availableModes}
              value={role.mode}
              onChange={(value) => onChange({ ...role, mode: value })}
            />
          </Flex>
        </div>

        <FormTabs
          forms={formsForMode}
          activeId={activeForm?.id}
          onSelect={setActiveFormId}
          onAdd={addForm}
          canAdd={formsForMode.length < MAX_FORMS_PER_MODE}
          onRename={async (formId, name) => {
            const target = role.forms.find((f) => f.id === formId);
            if (!target || target.name === name) return;
            updateForm(formId, { ...target, name });
            if (isPersistedId(formId)) {
              try {
                await saveForm(formId, { name });
              } catch {
                updateForm(formId, target);
              }
            }
          }}
          renderContent={(form) => (
            <SectionTabs
              form={form}
              sections={form.sections}
              formVersion={form.version}
              erroredSectionIds={erroredSectionIds}
              activeId={
                form.id === activeForm?.id
                  ? activeSectionId
                  : form.sections[0]?.id
              }
              onSelect={setActiveSectionId}
              onAdd={addSection}
              onRename={(sectionId, title) =>
                updateForm(form.id, {
                  ...form,
                  sections: form.sections.map((s) =>
                    s.id === sectionId ? { ...s, title } : s
                  ),
                })
              }
              onDelete={deleteSection}
              onToggleRepeatable={async (sectionId, repeatable) => {
                try {
                  await setFormRepeatable(form.id, repeatable);
                  updateForm(form.id, {
                    ...form,
                    repeatable,
                    sections: form.sections.map((s) =>
                      s.id === sectionId ? { ...s, repeatable } : s
                    ),
                  });
                } catch {
                  /* error handled in hook */
                }
              }}
            />
          )}
        />

        <Flex vertical gap={12} style={{ padding: "16px 24px" }}>
          {activeSection && (
            <SectionEditor
              section={activeSection}
              onChange={updateSection}
              basePath={["sections", activeSectionIdx]}
            />
          )}
        </Flex>

        <Flex
          justify="space-between"
          align="center"
          gap={8}
          wrap="wrap"
          style={{
            marginTop: "auto",
            padding: "10px 24px",
            paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))",
            borderTop: "1px solid #eaecf0",
            position: "sticky",
            bottom: 0,
            background: "#ffffff",
            zIndex: 5,
          }}
        >
          {activeForm && !activeForm.system ? (
            <Space size={8}>
              <Typography.Text>Deactivate</Typography.Text>
              <Switch
                size="small"
                loading={updating}
                checked={activeForm.active === false}
                onChange={async (checked) => {
                  const isActive = !checked;
                  try {
                    await setFormActive(activeForm.id, isActive);
                    updateForm(activeForm.id, {
                      ...activeForm,
                      active: isActive,
                    });
                  } catch {
                    /* error toast handled in hook */
                  }
                }}
              />
            </Space>
          ) : (
            <span />
          )}
          <Space>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
            <Button
              icon={<CopyOutlined />}
              disabled={
                !activeForm ||
                !isPersistedId(activeForm.id) ||
                roles.length <= 1
              }
              onClick={() => setDuplicateOpen(true)}
            >
              Duplicate to role
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={updating}
              disabled={!activeForm}
              onClick={handleSave}
            >
              Save
            </Button>
          </Space>
        </Flex>

        <PreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          role={role}
          forms={formsForMode}
          initialFormId={activeForm?.id}
        />

        <DuplicateFormModal
          open={duplicateOpen}
          form={activeForm}
          sourceRole={role}
          roles={roles}
          loading={duplicating}
          onCancel={() => setDuplicateOpen(false)}
          onConfirm={handleDuplicate}
        />
      </Flex>
    </FormValidationProvider>
  );
};

export default FormBuilder;
